/* eslint-disable no-unreachable */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Col,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import classNames from 'classnames';
import { AddButton } from 'components/buttons/add-button';
import { Header } from 'components/header';
import { TrashIcon } from 'components/icons';
import { Select, SelectOption } from 'components/select';
import { useEmployee } from 'hooks';
import { useDomainList } from 'hooks/use-domains-dictionary';
import { useSaveDomains } from 'hooks/use-save-domains';
import { EpDomain } from 'types/graphql-types';
import styles from './styles.less';

export type DomainsFormProps = {
  editMode?: boolean;
  formName?: string;
  setEditMode?: (value: boolean) => void;
};

const HORIZONTAL_GAP = 0;
const VERTICAL_GAP = 6;

const MAX_DOMAIN_LENGTH = 30;

const renderView = (key: string, domain?: EpDomain): React.ReactNode => (
  <Row
    key={`${key}-${domain?.subDomains?.toString() || ''}`}
    gutter={[HORIZONTAL_GAP, VERTICAL_GAP]}
    className={classNames(styles['row'], styles['data-row'])}
  >
    <Col span={13}>
      {domain?.desc === 'EP' ? (
        <Tag>{domain?.name}</Tag>
      ) : (
        <Tooltip
          placement="bottom"
          title={
            'You added this domain yourself. So far, only you can see this skill.'
          }
        >
          <Tag className={styles['custom-domain']}>{domain?.name}</Tag>
        </Tooltip>
      )}
    </Col>
    <Col span={11}>
      <div className={styles['sphere']}>{domain?.subDomains?.[0]}</div>
    </Col>
  </Row>
);

const RenderField: FC<{
  name: number;
  options: SelectOption[];
  onDelete: () => void;
  onChange: () => void;
  form: FormInstance;
}> = ({ name, options, onChange, onDelete, form }) => {
  const [customOption, setCustomOption] = useState<undefined | string>(
    undefined,
  );

  const onSearch = (value: string): void => {
    setCustomOption(value);
  };

  const onChangeSelect = (newValue: string): void => {
    const { domains: values } = form.getFieldsValue() as {
      domains: {
        domain: string;
        sphere: string;
      }[];
    };
    const duplicate = values.find(
      (domain, i) =>
        domain.domain.toUpperCase() === newValue.toUpperCase() && i !== name,
    );

    if (duplicate) {
      form.setFieldValue(['domains', name, 'domain'], '');
      form.setFields([
        {
          errors: ['This domain is already in use'],
          name: ['domains', name, 'domain'],
        },
      ]);
      setCustomOption(undefined);
    } else {
      form.setFields([
        {
          errors: [],
          name: ['domains', name, 'domain'],
        },
      ]);
      onChange();
    }
  };

  const updatedOptions = [
    ...(customOption
      ? [
          {
            label: customOption,
            value: customOption,
          },
        ]
      : []),
    ...options,
  ];

  return (
    <Row
      gutter={[HORIZONTAL_GAP, VERTICAL_GAP]}
      className={classNames(styles['row'], styles['data-row'])}
    >
      <Col span={13}>
        <Form.Item name={[name, 'domain']} className={styles['form-item']}>
          <Select
            options={updatedOptions}
            onChange={(value) => onChangeSelect(value as string)}
            showSearch
            onSearch={onSearch}
            placeholder={'Select your domains'}
          />
        </Form.Item>
      </Col>
      <Col span={11}>
        <div className={styles['sphere']}>
          <Form.Item
            name={[name, 'sphere']}
            className={styles['form-item']}
            rules={[
              {
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                max: 250,
                message: 'Subdomain should not exceed 250 characters',
              },
            ]}
          >
            <Input
              placeholder="Enter some subdomains in which you are experienced"
              maxLength={250}
            />
          </Form.Item>
          <TrashIcon
            onClick={() => {
              onDelete();
            }}
          />
        </div>
      </Col>
    </Row>
  );
};

const RenderFormList: FC<{
  initialValues: unknown[];
  options: SelectOption[];
  onAddClick: () => void;
  onChange: () => void;
  form: FormInstance;
}> = ({ options, onAddClick, initialValues, onChange, form }) => {
  const onDelete = (onConfirm: () => void): void =>
    void Modal.confirm({
      content: 'Are you sure you want to delete this domain?',
      okText: 'Yes, delete',
      onOk: onConfirm,
      title: 'Confirm Action',
    });

  return (
    <Form.List name="domains" initialValue={initialValues}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field) => (
            <RenderField
              key={field.key}
              name={field.name}
              options={options}
              onDelete={() =>
                onDelete(() => {
                  remove(field.name);
                  onChange();
                })
              }
              onChange={onChange}
              form={form}
            />
          ))}
          <AddButton
            onClick={() => {
              add();
              onAddClick();
            }}
          />
        </>
      )}
    </Form.List>
  );
};

export const DomainsForm: FC<DomainsFormProps> = (props) => {
  const { editMode = false, formName, setEditMode } = props;

  const [form] = Form.useForm();

  const { t } = useTranslation();

  const { employee, error } = useEmployee();
  const domains = useMemo(() => employee?.domains || [], [employee?.domains]);

  const { data } = useDomainList();
  const domainDictionary = useMemo(
    () => data?.domainList || [],
    [data?.domainList],
  );

  useEffect(() => {
    if (!editMode) {
      form.resetFields();
    }
  }, [editMode, form]);

  const initialValue: object[] = domains.map((domain) => ({
    domain: domain.name,
    sphere: domain.subDomains?.[0],
  }));

  const [availableDomains, setAvailableDomains] = useState(
    domainDictionary?.filter(
      (d) => !domains.find((domain) => domain.name === d),
    ),
  );

  useEffect(() => {
    setAvailableDomains(
      domainDictionary?.filter(
        (d) => !domains.find((domain) => domain.name === d),
      ),
    );
  }, [editMode, domainDictionary, domains]);

  const saveDomains = useSaveDomains();

  if (!employee || error) {
    return <Alert message={error?.message} showIcon type="error" />;
  }

  if (domains.length === 0 && !editMode) {
    return t('Add project domains in which you are experienced');
  }

  const onSelectChange = (): void => {
    const values = form?.getFieldsValue() as {
      domains: { domain: string; sphere: string }[];
    };

    const takenDomains = values.domains.map((domain) => domain.domain);

    setAvailableDomains(
      domainDictionary?.filter((d) => !takenDomains.includes(d)),
    );
  };

  const availableOptions =
    availableDomains?.map((domain) => ({
      label: domain,
      value: domain,
    })) || [];

  const onFormSubmit = ({
    domains: fields,
  }: {
    domains: { domain: string; sphere?: string }[];
  }): void => {
    let hasErrors = false;

    fields.forEach((field, i) => {
      if (!field.domain && field.sphere) {
        form.setFields([
          {
            errors: ['Domain is mandatory'],
            name: ['domains', i, 'domain'],
          },
        ]);
        hasErrors = true;
      }

      if (
        field.domain &&
        !domainDictionary.includes(field.domain) &&
        field.domain.length > MAX_DOMAIN_LENGTH
      ) {
        form.setFields([
          {
            errors: ['Domain should not exceed 30 characters'],
            name: ['domains', i, 'domain'],
          },
        ]);
        hasErrors = true;
      }
    });

    if (hasErrors) {
      return;
    }

    const filteredFields = fields.filter((field) => field.domain);

    const domainsToSave = filteredFields.map(({ domain, sphere }) => ({
      desc: domainDictionary.includes(domain) ? 'EP' : employee.login,
      name: domain,
      subDomains: sphere ? [sphere] : [],
    }));

    void saveDomains({ domains: domainsToSave })
      .then(() => {
        notification.success({
          message: t('Domains have been saved!'),
        });
        setEditMode && setEditMode(false);
      })
      .catch(() =>
        notification.error({
          message: t('Something has gone wrong! Try again!'),
        }),
      );
  };

  const headers = (
    <Row className={classNames(styles['row'], styles['header-row'])}>
      <Col span={13}>
        <Header level={5} className={styles['header']}>
          {t('Domain name')}
        </Header>
      </Col>
      <Col span={11}>
        <Header level={5} className={styles['header']}>
          {t('Sphere')}
        </Header>
      </Col>
    </Row>
  );

  return (
    <Row>
      <Col span={19}>
        {editMode ? (
          <Form name={formName} onFinish={onFormSubmit} form={form}>
            {headers}
            <RenderFormList
              key="memoed"
              initialValues={initialValue}
              options={availableOptions}
              onAddClick={() => setEditMode && setEditMode(true)}
              onChange={onSelectChange}
              form={form}
            />
          </Form>
        ) : (
          <>
            {headers}
            {domains.map((domain) => renderView(domain.name, domain))}
          </>
        )}
      </Col>
    </Row>
  );
};
